/**
 * The external services this application depends on.
 *
 * A SERVICE IS NOT A LIBRARY. `stack.ts` lists npm packages — things with a
 * version number. This lists accounts: things with credentials, a dashboard, a
 * monthly bill, a rate limit, and the ability to be down at three in the
 * morning. Some appear in both (resend the package, Resend the account); some
 * only here (Postgres, Railway), because they are not npm packages at all.
 *
 * WHY THIS EXISTS. Adding Twilio nine months from now means adding a
 * dependency, some env vars and an adapter. Only the dependency was caught by
 * `stack.ts`, and the setup checklist went on reporting "ready for real users"
 * while SMS was entirely unconfigured. A checklist that lies is worse than no
 * checklist.
 *
 * THE ENFORCEMENT ANCHOR IS `env`. You cannot call an external service without
 * credentials, so every variable in `server/env.ts` must be claimed either by a
 * service here or by `coreEnvVars` below. That is not a convention anyone has
 * to remember — it is the one step that cannot be skipped, and the test fails
 * until the service is described.
 *
 * Registering a service is also what puts it on the setup checklist (`checks`)
 * and gives its usage and spend a home (`usage`, `plan`).
 */
import type { CheckId } from '@/core/setup'

/** One currency for now. Supporting several is a bigger change than it looks. */
export const CURRENCY = 'USD'

export interface ServiceUsageMetric {
  /** The id stored against each monthly total. Stable — it is a database key. */
  id: string
  /** What is being counted, as a person would say it. */
  label: string
  /** How the vendor bills it, so a number here maps to the invoice. */
  billingNote: string
  /**
   * The file that calls `recordUsage` for this metric.
   *
   * Declared, and checked by `services.test.ts`: the file must exist and must
   * really contain that call. A metric nothing increments shows a permanent
   * flat zero, which reads as "we are not using this service" while the invoice
   * says otherwise — the same lie the setup checklist used to tell, and it was
   * live here: three of five metrics were never recorded. The System page shows
   * this path, so you can see exactly which code is spending your money.
   */
  countedAt: string
  /**
   * List price, for estimating spend.
   *
   * `perUnit: null` means "nobody has established this yet" and must be paired
   * with `unpricedReason`, so that not having a price is a recorded decision
   * rather than a default nobody ever revisits. An explicit null is also
   * required rather than a missing field, because a missing price silently
   * becomes zero and understates the bill.
   */
  price: {
    perUnit: number | null
    includedFree: number
    /** Required whenever perUnit is null. Say why, in a sentence. */
    unpricedReason?: string
  }
}

export interface ServicePlan {
  name: string
  /** The fixed monthly fee before any metered usage. null when not priced yet. */
  monthlyFee: number | null
  /**
   * Usage the fee already pays for, in dollars — Railway's Pro plan covers the
   * first $20 of usage. Usage costs up to this are covered; only the rest is
   * charged. Omit when the plan has no such credit.
   */
  includedCredit?: number
}

/**
 * How to get a new key when the old one stops working — System → Services →
 * Reconnect (docs/SYSTEM.md → Reconnect, D-019).
 *
 * Only for keys a person makes at the provider and pastes in. A variable that
 * is a Railway reference (the database, the bucket) is never pasted: its fix
 * is in Railway, and the failing check's own steps say how.
 */
export interface ServiceReconnect {
  /** Where the provider makes a new key. */
  keyPage: { label: string; url: string }
  /** What to do there, in order, ending with the key copied. */
  steps: string[]
  /**
   * The variables Reconnect may write. Each must be one of the service's own
   * `env`, and never a reference or the app's own configuration —
   * `reconnect.test.ts` holds both.
   */
  variables: { name: string; label: string }[]
}

export interface ServiceEntry {
  id: string
  name: string
  /**
   * What kind of thing this is, in two or three words — 'transactional email',
   * 'error monitoring'. Shown in parentheses after the name, because "Resend"
   * means nothing to somebody reading the bill in six months.
   */
  category: string
  /** What the app uses it for. */
  purpose: string
  /** True when real users cannot be served without it. */
  required: boolean
  /** The env vars it needs. THE ENFORCEMENT ANCHOR — see the file comment. */
  env: string[]
  /** Setup checks that prove it is actually connected. */
  checks: CheckId[]
  /** In plain language, what stops working when it is missing. */
  whatBreaks: string
  dashboardUrl: string
  statusUrl?: string
  docsUrl: string
  /** What to count each month. Omit when there is nothing this app can honestly meter. */
  usage?: ServiceUsageMetric[]
  /** The plan you are on, for the fixed part of the bill. */
  plan?: ServicePlan
  /**
   * When the prices above were last checked against the vendor's pricing page,
   * as YYYY-MM-DD. REQUIRED, and shown on the page — a spend estimate built on
   * two-year-old list prices is a guess wearing a suit, and vendors change
   * their pricing without telling you.
   */
  pricesCheckedOn: string
  /** How Reconnect gets a new key, when this service has one to paste. */
  reconnect?: ServiceReconnect
}

/**
 * Variables that configure the app itself rather than any external account.
 * Keep this list short: if something here actually belongs to a vendor, it
 * belongs in a service instead.
 */
export const coreEnvVars = [
  'NODE_ENV',
  'API_PORT',
  'PORT',
  'DATA_SOURCE',
  'LOG_LEVEL',
  'EXPOSE_API_DOCS',
  'RELEASE',
  'PREVIEW_PASSWORD',
  // Better Auth runs inside this app rather than as a hosted service, so its
  // secret and public URL are this app's own configuration.
  'BETTER_AUTH_SECRET',
  'BETTER_AUTH_URL',
  // Trusted email domains to start with — this app's own access setting,
  // applied once (docs/PERMISSIONS.md → "Automatic access").
  'AUTO_JOIN_DOMAINS',
  // The server half of letting the app message Majik's people — a switch on
  // this app, not a vendor account (ADR outbound-recipient-guard).
  'MAJIK_OUTBOUND',
  // Which environment this is, and — in sandbox — where production lives
  // (docs/ENVIRONMENTS.md). Our own deployments, not a vendor's.
  'VITE_ENVIRONMENT',
  'PRODUCTION_URL',
  // Data copy: our own environments talking to each other, not a vendor
  // (docs/DATA-COPY.md).
  'DATA_COPY_SECRET',
  'DATA_COPY_HISTORY_MONTHS',
  // Where this app's own webhooks answer (ADR inbound-webhooks) — our
  // address, not a vendor's.
  'WEBHOOK_BASE_URL',
  'WEBHOOK_ID_PREFIX',
  'WEBHOOK_SANDBOX_URL',
]

export const services: ServiceEntry[] = [
  {
    id: 'postgres',
    name: 'Postgres',
    category: 'database',
    purpose: 'Stores every record. Provisioned by Railway alongside the app.',
    required: true,
    env: ['DATABASE_URL', 'DB_STATEMENT_TIMEOUT_MS'],
    checks: ['database', 'migrations'],
    whatBreaks: 'Nothing can be saved or read. The app runs on fixtures only.',
    dashboardUrl: 'https://railway.com/dashboard',
    statusUrl: 'https://railway.instatus.com',
    docsUrl: 'https://docs.railway.com/guides/postgresql',
    // No meter of its own: Postgres runs on Railway, so its memory, CPU and
    // disk are inside Railway's usage below and billed there. Counting it here
    // too would bill it twice.
    plan: { name: 'Billed through Railway', monthlyFee: 0 },
    pricesCheckedOn: '2026-09-19',
  },
  {
    id: 'railway',
    name: 'Railway',
    category: 'hosting',
    purpose:
      'Runs the app and its database. Usage — memory, CPU, data sent out and disk — is read from Railway itself.',
    required: false,
    // RAILWAY_ENVIRONMENT_ID, RAILWAY_SERVICE_ID and RAILWAY_DEPLOYMENT_ID are set by Railway itself;
    // with RAILWAY_RECONNECT_TOKEN they let Reconnect save a new key into this
    // service's own variables (D-019).
    env: [
      'RAILWAY_API_TOKEN',
      'RAILWAY_PROJECT_ID',
      'RAILWAY_ENVIRONMENT_ID',
      'RAILWAY_SERVICE_ID',
      'RAILWAY_DEPLOYMENT_ID',
      'RAILWAY_RECONNECT_TOKEN',
    ],
    checks: ['hosting-usage'],
    whatBreaks:
      'Without the token, nothing breaks for users — the System page just can’t show what hosting costs.',
    dashboardUrl: 'https://railway.com/dashboard',
    statusUrl: 'https://railway.instatus.com',
    docsUrl: 'https://docs.railway.com/pricing/plans',
    // Read from Railway's API, not counted by us — see src/adapters/railway.
    // Rates from Gideon's Railway Usage page, 2026-09-19.
    usage: [
      {
        id: 'memory_gb_minutes',
        label: 'Memory',
        billingNote: 'GB of memory held, per minute',
        countedAt: 'src/adapters/railway/usage.ts',
        price: { perUnit: 0.000231, includedFree: 0 },
      },
      {
        id: 'cpu_vcpu_minutes',
        label: 'CPU',
        billingNote: 'vCPU used, per minute',
        countedAt: 'src/adapters/railway/usage.ts',
        price: { perUnit: 0.000463, includedFree: 0 },
      },
      {
        id: 'egress_gb',
        label: 'Data sent out',
        billingNote: 'GB leaving Railway (egress)',
        countedAt: 'src/adapters/railway/usage.ts',
        price: { perUnit: 0.05, includedFree: 0 },
      },
      {
        id: 'volume_gb_minutes',
        label: 'Disk',
        billingNote: 'GB of volume storage, per minute',
        countedAt: 'src/adapters/railway/usage.ts',
        price: { perUnit: 0.00000347, includedFree: 0 },
      },
    ],
    // Usage only, on purpose. The Pro subscription ($20/month, which includes
    // $20 of usage) is Gideon's and is spread across several projects, so
    // charging it to this app would overstate what this app costs. What this
    // page answers is "what is THIS app consuming", at Railway's rates.
    plan: { name: 'Pro (shared across projects)', monthlyFee: 0 },
    pricesCheckedOn: '2026-09-19',
    reconnect: {
      keyPage: { label: 'Railway tokens', url: 'https://railway.com/account/tokens' },
      steps: [
        'In Railway, open Account settings → Tokens.',
        'Create a token and choose the workspace this project is in, not “No workspace”. A project token won’t work: it can’t read usage.',
        'Copy it. Railway shows it once.',
      ],
      variables: [{ name: 'RAILWAY_API_TOKEN', label: 'Workspace token' }],
    },
  },
  {
    id: 'anthropic',
    name: 'Anthropic',
    category: 'AI model',
    purpose:
      'Claude, behind the assistant in the bottom-right corner. Haiku 4.5 by default — the fastest, cheapest current model.',
    required: false,
    env: ['ANTHROPIC_API_KEY', 'ASSISTANT_MODEL'],
    checks: ['assistant-key'],
    whatBreaks: 'The assistant says it’s unavailable. Nothing else in the app depends on it.',
    dashboardUrl: 'https://platform.claude.com',
    statusUrl: 'https://status.anthropic.com',
    docsUrl: 'https://platform.claude.com/docs/en/about-claude/pricing',
    // Prices per token for Claude Haiku 4.5, from Anthropic's pricing page on
    // 2026-09-19 ($1 / $5 per million input / output; cache reads $0.10,
    // 5-minute cache writes $1.25). Prepaid credits: no monthly fee.
    usage: [
      {
        id: 'input_tokens',
        label: 'Input tokens',
        billingNote: 'Text sent to Claude — the question, the history, tool results',
        countedAt: 'src/adapters/ai/claude.ts',
        price: { perUnit: 0.000001, includedFree: 0 },
      },
      {
        id: 'output_tokens',
        label: 'Output tokens',
        billingNote: 'Text Claude writes back',
        countedAt: 'src/adapters/ai/claude.ts',
        price: { perUnit: 0.000005, includedFree: 0 },
      },
      {
        id: 'cache_read_tokens',
        label: 'Cached input read',
        billingNote: 'Repeated instructions and tools, read from cache at a tenth of the price',
        countedAt: 'src/adapters/ai/claude.ts',
        price: { perUnit: 0.0000001, includedFree: 0 },
      },
      {
        id: 'cache_write_tokens',
        label: 'Cached input written',
        billingNote: 'Instructions and tools stored in the 5-minute cache',
        countedAt: 'src/adapters/ai/claude.ts',
        price: { perUnit: 0.00000125, includedFree: 0 },
      },
    ],
    plan: { name: 'Pay as you go (prepaid credits)', monthlyFee: 0 },
    pricesCheckedOn: '2026-09-19',
    reconnect: {
      keyPage: {
        label: 'Claude Console API keys',
        url: 'https://platform.claude.com/settings/keys',
      },
      steps: [
        'Sign in to the Claude Console. This is the API account, not a Claude.ai subscription.',
        'API keys → Create key, in this app’s workspace.',
        'Copy it. It’s shown once.',
      ],
      variables: [{ name: 'ANTHROPIC_API_KEY', label: 'API key' }],
    },
  },
  {
    id: 'resend',
    name: 'Resend',
    category: 'transactional email',
    purpose: 'Sends verification, password reset and invitation email.',
    required: true,
    env: ['RESEND_API_KEY', 'EMAIL_FROM'],
    checks: ['email-key', 'email-domain'],
    whatBreaks:
      'No verification email is sent — and because verification is required, nobody can finish signing up.',
    dashboardUrl: 'https://resend.com/overview',
    statusUrl: 'https://resend-status.com',
    docsUrl: 'https://resend.com/docs',
    usage: [
      {
        id: 'emails_sent',
        label: 'Emails sent',
        billingNote: 'Resend bills per email, with a monthly free allowance.',
        countedAt: 'src/adapters/email/index.ts',
        price: {
          perUnit: null,
          includedFree: 3000,
          unpricedReason:
            'The Resend account exists (sending from interautomate.com, connected 2026-09-19), but Gideon has not yet confirmed which plan it is on. Set it from Set pricing on the Services tab once he has.',
        },
      },
      {
        id: 'emails_failed',
        label: 'Failed to send',
        billingNote:
          'Requests Resend rejected outright. Assumed unbilled, since the message was never accepted — worth confirming against an invoice. These are NOT delivery bounces: those happen after Resend accepts a message and would need a webhook this app does not have yet.',
        countedAt: 'src/adapters/email/index.ts',
        price: { perUnit: 0, includedFree: 0 },
      },
    ],
    plan: { name: 'Free', monthlyFee: 0 },
    pricesCheckedOn: '2026-09-11',
    reconnect: {
      keyPage: { label: 'Resend API keys', url: 'https://resend.com/api-keys' },
      steps: [
        'In Resend, open API Keys → Create API key.',
        'Permission: Sending access, for the domain this app sends from. That’s the safest setting, and it’s enough.',
        'Copy it. Resend shows it once.',
      ],
      variables: [{ name: 'RESEND_API_KEY', label: 'API key' }],
    },
  },
  {
    id: 'posthog',
    name: 'PostHog',
    category: 'error tracking and replay',
    purpose: 'Catches crashes, records the session that broke, and says which person hit it.',
    required: false,
    // VITE_ vars are baked into the browser bundle, so they are not in the
    // server env schema — but POSTHOG_API_KEY is, because the server reports
    // its own crashes.
    env: ['POSTHOG_API_KEY', 'POSTHOG_HOST'],
    checks: ['errors', 'analytics'],
    whatBreaks:
      'Crashes are invisible. You find out from a user, with no recording of what they did and no stack trace.',
    dashboardUrl: 'https://posthog.com',
    statusUrl: 'https://status.posthog.com',
    docsUrl: 'https://posthog.com/docs',
    usage: [
      {
        id: 'server_exceptions',
        label: 'Server exceptions reported',
        billingNote:
          'PostHog bills exceptions per event, browser and server together, against one monthly allowance. Only the server’s are counted here — browser exceptions and session replays go straight from the visitor to PostHog and never pass through this app, so PostHog’s own dashboard is the full picture and the one the invoice follows.',
        countedAt: 'src/adapters/observability/posthog.server.ts',
        price: {
          // posthog.com/docs/error-tracking/pricing, checked 2026-09-21: the
          // first 100,000 exceptions each month are free, then $0.00037 each
          // up to 325,000. The free allowance is shared with browser
          // exceptions, which this app cannot see — so a bill can start before
          // this counter alone reaches 100,000.
          perUnit: 0.00037,
          includedFree: 100_000,
        },
      },
    ],
    // Pay-as-you-go: PostHog charges no platform fee and no per-seat fee, so
    // the fixed part of the bill really is zero and everything is usage.
    plan: { name: 'Pay-as-you-go', monthlyFee: 0 },
    pricesCheckedOn: '2026-09-21',
    reconnect: {
      keyPage: {
        label: 'PostHog project settings',
        url: 'https://us.posthog.com/settings/project',
      },
      steps: [
        'In PostHog, open Settings → Project → General.',
        'Copy the Project API key. It starts with phc_.',
        'This is the key the server reports its own crashes with. The browser’s copy is built into the app, so if the project itself changed, ask Claude to change that too.',
      ],
      variables: [{ name: 'POSTHOG_API_KEY', label: 'Project API key' }],
    },
  },
  {
    id: 'betterstack',
    name: 'Better Stack',
    category: 'uptime and alerting',
    purpose:
      'Watches from outside (docs/OBSERVABILITY.md "Watching feeds with Better Stack"): polls /readyz and /api/health/feeds every 3 minutes and emails the team when the app is down or a webhook’s data stops landing; a heartbeat per scheduled job (meters, data copy) catches one that stopped running. The app sends no alert email of its own.',
    required: false,
    // Its heartbeat addresses (BETTERSTACK_HEARTBEAT_URL_<JOB>) are read by
    // the scheduled jobs, not the web server, so none is in server/env.ts.
    env: [],
    checks: [],
    whatBreaks:
      'Nothing for users. Without it nobody is emailed when the app is down, a webhook goes quiet or a nightly job stops — only the Admin banner and System → Setup say so, to whoever looks.',
    dashboardUrl: 'https://uptime.betterstack.com',
    statusUrl: 'https://status.betterstack.com',
    docsUrl: 'https://betterstack.com/docs/uptime/',
    // Not priced for this app yet: whether it runs on the free plan or a paid
    // one is decided when an account is opened (the pricing gate asks then).
    plan: { name: 'Not chosen yet', monthlyFee: null },
    pricesCheckedOn: '2026-10-24',
  },
  {
    id: 'change-videos',
    name: 'Change-video bucket',
    category: 'object storage',
    purpose:
      'Holds the explainer video, poster and captions for each change-log entry (System → Change). A “change-videos” bucket in each environment; a video goes to the one where its change is live (D-016). Any S3-compatible bucket works.',
    required: false,
    env: [
      'CHANGE_VIDEOS_ENDPOINT',
      'CHANGE_VIDEOS_BUCKET',
      'CHANGE_VIDEOS_REGION',
      'CHANGE_VIDEOS_ACCESS_KEY_ID',
      'CHANGE_VIDEOS_SECRET_ACCESS_KEY',
      'CHANGE_VIDEOS_URL_STYLE',
    ],
    checks: ['change-videos'],
    whatBreaks:
      'Only the videos: none show on System → Change. The change log itself, and everything else, carry on.',
    dashboardUrl: 'https://railway.com/dashboard',
    docsUrl: 'https://docs.railway.com/storage-buckets/billing',
    // Deliberately unpriced: which provider holds the bucket (Railway, R2,
    // S3…) and what it charges is a choice each project makes. Videos are a
    // few MB each, so the amount is small, but the pricing gate says never
    // to guess it — set the plan on this page, or answer /add-service's
    // questions, when the bucket exists. Uploads happen from a laptop
    // (`pnpm changes:video`), not through this app, so there is no metric.
    plan: { name: 'Not priced yet — depends on the bucket provider', monthlyFee: null },
    pricesCheckedOn: '2026-10-24',
  },
  {
    id: 'elevenlabs',
    name: 'ElevenLabs',
    category: 'AI voiceover',
    purpose:
      'The narrator’s voice on each change-log video. Called by the background video job on the owner’s computer, through the ElevenLabs connector, never by this app — so it needs no key here.',
    required: false,
    env: [],
    checks: [],
    whatBreaks:
      'Nothing in the app. Live changes say “Video coming soon” until a video can be made.',
    dashboardUrl: 'https://elevenlabs.io/app/subscription',
    statusUrl: 'https://status.elevenlabs.io',
    docsUrl: 'https://elevenlabs.io/pricing/api',
    // Deliberately unpriced: the plan is the owner's own and may be shared
    // across their work, and the per-character rate depends on it — measure
    // it on the first video rather than copying a number. This app never
    // calls ElevenLabs, so there is no metric.
    plan: { name: 'The owner’s own plan — not priced yet', monthlyFee: null },
    pricesCheckedOn: '2026-10-24',
  },
]

export const servicesById = new Map(services.map((service) => [service.id, service]))

/** Every env var claimed by a service. Used by the enforcement test. */
export const claimedEnvVars = new Set(services.flatMap((service) => service.env))

/**
 * Whether the app is actually talking to a service.
 *
 * Derived from the setup checks the service already declares — not a second
 * source of truth that could disagree with /setup. A service with no checks, or
 * whose checks were skipped, is 'unknown' rather than quietly 'connected':
 * saying "we don't know" is the honest answer, and it looks different on the
 * page from a confirmed green.
 */
export type ConnectionState = 'connected' | 'not-connected' | 'unknown'

export function connectionStates(
  entries: readonly ServiceEntry[],
  results: readonly { id: string; status: string }[],
): Map<string, ConnectionState> {
  const byCheck = new Map(results.map((result) => [result.id, result.status]))
  return new Map(
    entries.map((service): [string, ConnectionState] => {
      const statuses = service.checks.map((id) => byCheck.get(id))
      if (statuses.length === 0 || statuses.some((status) => status === undefined)) {
        return [service.id, 'unknown']
      }
      if (statuses.some((status) => status === 'fail')) return [service.id, 'not-connected']
      if (statuses.every((status) => status === 'pass')) return [service.id, 'connected']
      return [service.id, 'unknown']
    }),
  )
}

/** Prices older than this are called out on the page as worth re-checking. */
export const PRICE_REVIEW_MONTHS = 12

export interface PricingState {
  serviceId: string
  name: string
  /** Whole months since the prices were last checked. */
  monthsOld: number
  stale: boolean
  checkedOn: string
  /** Metric labels with no established price. */
  unpriced: string[]
  /** True when the plan fee is unknown too. */
  planUnpriced: boolean
}

/**
 * Which services still need somebody to establish their pricing, and which
 * have prices old enough to be worth re-checking.
 *
 * Pure, so the page and the test agree on the answer rather than each having
 * their own idea of what "needs pricing" means.
 */
export function pricingStates(entries: readonly ServiceEntry[], now = new Date()): PricingState[] {
  return entries
    .filter((service) => service.plan || (service.usage?.length ?? 0) > 0)
    .map((service) => {
      const checked = new Date(`${service.pricesCheckedOn}T00:00:00Z`)
      const monthsOld = Number.isNaN(checked.getTime())
        ? Number.POSITIVE_INFINITY
        : Math.floor((now.getTime() - checked.getTime()) / (1000 * 60 * 60 * 24 * 30.44))
      return {
        serviceId: service.id,
        name: service.name,
        monthsOld,
        stale: monthsOld >= PRICE_REVIEW_MONTHS,
        checkedOn: service.pricesCheckedOn,
        unpriced: (service.usage ?? [])
          .filter((metric) => metric.price.perUnit === null)
          .map((metric) => metric.label),
        planUnpriced: service.plan?.monthlyFee === null,
      }
    })
}

/** Services that nobody has priced yet. The dashboard asks you to fix these. */
export function servicesNeedingPricing(
  entries: readonly ServiceEntry[],
  now = new Date(),
): PricingState[] {
  return pricingStates(entries, now).filter(
    (state) => state.unpriced.length > 0 || state.planUnpriced || state.stale,
  )
}
